'use client';

import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
}

const sizeClasses: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  className,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const descId = `${dialogId}-description`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    closeOnEscapeRef.current = closeOnEscape;
  }, [closeOnEscape]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (closeOnEscapeRef.current && e.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      // Focus trap: wrap Tab navigation within the dialog
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    const focusFrame = requestAnimationFrame(() => {
      dialogRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const content = (
    <div
      // z-[200]: page overlays such as the roster full-screen workspace sit at
      // z-[100]. This dialog portals to document.body, so z-50 painted behind
      // those overlays and hid unassign/cancel/swap from the roster grid.
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
    >
      {/* Backdrop */}
      <div
        className="experience-candidate-dialog-backdrop experience-candidate-motion fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Dialog panel — BUG-1: constrain to viewport with a scrollable body
          so tall wizards can't push their footer off-screen on short screens */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'experience-candidate-dialog-panel relative z-10 flex max-h-[calc(100vh-1.5rem)] w-full min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[calc(100vh-2rem)]',
          'experience-candidate-motion animate-in fade-in-0 zoom-in-95 duration-200',
          sizeClasses[size],
          className,
        )}
      >
        {/* Header — fixed, never scrolls */}
        {(title || showCloseButton) && (
          <div className="experience-candidate-dialog-header flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-6">
            <div className="min-w-0">
              {title && (
                <h2
                  id={titleId}
                  className="experience-candidate-heading min-w-0 break-words font-heading text-card-title text-text-heading"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={descId}
                  className="experience-candidate-muted mt-1 break-words font-body text-body-sm text-text-muted"
                >
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="experience-candidate-dialog-close experience-candidate-motion experience-candidate-focus ml-4 inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-subtle transition-interactive duration-200 hover:bg-slate-100 hover:text-text-body"
                aria-label="Close dialog"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Body — scrollable region; footer stays visible at any viewport height */}
        <div className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-6">
          {children}
        </div>
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}

/* Convenience sub-components for dialog footer.
 * Sticky at the bottom of the scrollable dialog body so tall wizards can
 * never push the primary action out of view (BUG-1). */
export function DialogFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'experience-candidate-dialog-footer sticky bottom-0 z-10 -mx-4 -mb-4 mt-4 flex flex-col items-stretch gap-2 rounded-b-lg border-t border-slate-200 bg-surface-soft px-4 py-4 sm:-mx-6 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6',
        className,
      )}
    >
      {children}
    </div>
  );
}
